// Open-Meteo client. Free, no API key, generous rate limits, supports both
// forecast and historical (so the editor can fetch weather for an issue
// date that's a few days out OR a few days behind).
//
// Bangor, ME is wired in by default; could become a config later.

const BANGOR = { lat: 44.8016, lon: -68.7712 };

export interface WeatherFetchResult {
  todayTemp: string;
  todayLabel: string;
  todayIcon: string;
  todayHigh: string;
  todayLow: string;
  tomorrowTemp: string;
  tomorrowLabel: string;
  tomorrowIcon: string;
  tomorrowHigh: string;
  tomorrowLow: string;
  forecast: Array<{ day: string; high: string; low: string; icon: string }>;
  sunrise: string;
  sunset: string;
}

interface ApiResponse {
  current?: { temperature_2m: number; weather_code: number };
  daily?: {
    time: string[];
    weather_code: number[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    sunrise: string[];
    sunset: string[];
  };
}

export async function fetchWeather(issueDate: string): Promise<WeatherFetchResult> {
  // issueDate is YYYY-MM-DD. We pull a 6-day window starting that day so we
  // get today + tomorrow + 4 forecast days.
  const start = new Date(issueDate + 'T00:00:00');
  const end = new Date(start);
  end.setDate(end.getDate() + 5);
  const startStr = iso(start);
  const endStr = iso(end);

  const url = new URL('https://api.open-meteo.com/v1/forecast');
  url.searchParams.set('latitude', String(BANGOR.lat));
  url.searchParams.set('longitude', String(BANGOR.lon));
  url.searchParams.set('current', 'temperature_2m,weather_code');
  url.searchParams.set('daily', 'weather_code,temperature_2m_max,temperature_2m_min,sunrise,sunset');
  url.searchParams.set('temperature_unit', 'fahrenheit');
  url.searchParams.set('timezone', 'America/New_York');
  url.searchParams.set('start_date', startStr);
  url.searchParams.set('end_date', endStr);

  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Open-Meteo ${res.status}: ${await res.text().catch(() => '')}`);
  const data = (await res.json()) as ApiResponse;
  if (!data.daily || !data.current) throw new Error('Unexpected Open-Meteo response shape');

  const d = data.daily;
  const todayCode = d.weather_code[0] ?? data.current.weather_code;
  const todayLabel = wmoLabel(todayCode);
  const todayIcon = wmoIcon(todayCode);

  const tomorrowCode = d.weather_code[1] ?? todayCode;

  const todayMax = d.temperature_2m_max[0];
  const todayMin = d.temperature_2m_min[0];
  const tomorrowMax = d.temperature_2m_max[1] ?? todayMax;
  const tomorrowMin = d.temperature_2m_min[1] ?? todayMin;

  // The big number on each card is the midpoint of high and low, rounded.
  // More representative of "what it'll feel like" than the high alone, and
  // doesn't confusingly drift mid-day like Open-Meteo's `current` value.
  const todayTempNum = (todayMax + todayMin) / 2;
  const tomorrowTempNum = (tomorrowMax + tomorrowMin) / 2;

  const forecastItems: WeatherFetchResult['forecast'] = [];
  for (let i = 2; i < d.time.length; i++) {
    const dt = new Date(d.time[i] + 'T00:00:00');
    forecastItems.push({
      day: dt.toLocaleDateString('en-US', { weekday: 'short' }),
      high: `${Math.round(d.temperature_2m_max[i])}°`,
      low: `${Math.round(d.temperature_2m_min[i])}°`,
      icon: wmoIcon(d.weather_code[i]),
    });
  }

  return {
    todayTemp: `${Math.round(todayTempNum)}°`,
    todayLabel,
    todayIcon,
    todayHigh: `${Math.round(todayMax)}°`,
    todayLow: `${Math.round(todayMin)}°`,
    tomorrowTemp: `${Math.round(tomorrowTempNum)}°`,
    tomorrowLabel: wmoLabel(tomorrowCode),
    tomorrowIcon: wmoIcon(tomorrowCode),
    tomorrowHigh: `${Math.round(tomorrowMax)}°`,
    tomorrowLow: `${Math.round(tomorrowMin)}°`,
    forecast: forecastItems,
    sunrise: formatTime(d.sunrise[0]),
    sunset: formatTime(d.sunset[0]),
  };
}

function iso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatTime(s: string): string {
  // Open-Meteo gives "2026-04-25T05:36"
  const t = s.split('T')[1] || s;
  const [hStr, mStr] = t.split(':');
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${mStr} ${ampm}`;
}

// WMO weather code → user-facing label/icon. Reference:
// https://open-meteo.com/en/docs (table near the bottom).
function wmoLabel(code: number): string {
  if (code === 0) return 'Clear';
  if (code === 1) return 'Mainly clear';
  if (code === 2) return 'Partly cloudy';
  if (code === 3) return 'Overcast';
  if (code === 45 || code === 48) return 'Foggy';
  if (code >= 51 && code <= 57) return 'Drizzle';
  if (code >= 61 && code <= 67) return 'Rain';
  if (code >= 71 && code <= 77) return 'Snow';
  if (code >= 80 && code <= 82) return 'Showers';
  if (code === 85 || code === 86) return 'Snow showers';
  if (code === 95) return 'Thunderstorm';
  if (code === 96 || code === 99) return 'Thunder + hail';
  return 'Mixed';
}

function wmoIcon(code: number): string {
  if (code === 0 || code === 1) return '☀';
  if (code === 2) return '⛅';
  if (code === 3) return '☁';
  if (code === 45 || code === 48) return '🌫';
  if (code >= 51 && code <= 67) return '🌧';
  if (code >= 71 && code <= 77 || code === 85 || code === 86) return '❄';
  if (code >= 80 && code <= 82) return '🌧';
  if (code >= 95) return '⛈';
  return '☁';
}
